import { useState } from 'react'
import { useSelector } from 'react-redux'
import { ChevronDown, ChevronUp, Search, Edit2, Check, X, Merge, User } from 'lucide-react'
import type { PersonSummary, FaceListItem } from '@/store/api'
import type { RootState } from '@/store/store'
import { signedSrc } from '@/utils/media'
import { useSignedUrls } from '@/utils/useSignedUrls'
import FaceHoverPreview from './FaceHoverPreview'
import { useFaceHover } from './useFaceHover'

interface PersonCardProps {
  person: PersonSummary
  faces: FaceListItem[]
  onFindImages: (personId: string) => void
  onRename: (personId: string, name: string) => void
  onMerge: (personId: string) => void
  onRemoveFace?: (faceId: string) => void
  onMakePrimary?: (personId: string, faceId: string) => void
  onViewFace?: (faceId: string) => void
}

const protoSizeMap = {
  small: 'w-16 h-16',
  medium: 'w-24 h-24',
  large: 'w-32 h-32',
} as const

const faceSizeMap = {
  small: 'w-10 h-10',
  medium: 'w-12 h-12',
  large: 'w-16 h-16',
} as const

export default function PersonCard({
  person,
  faces,
  onFindImages,
  onRename,
  onMerge,
  onRemoveFace,
  onMakePrimary,
  onViewFace,
}: PersonCardProps) {
  useSignedUrls()
  const [expanded, setExpanded] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState(person.name ?? '')
  const gridSize = useSelector((state: RootState) => state.ui.gridSize)
  const { hoverState, handleMouseEnter, handleMouseLeave, handlePreviewEnter, handlePreviewLeave } = useFaceHover()

  const handleSaveName = () => {
    if (editName.trim()) {
      onRename(person.person_id, editName.trim())
    }
    setIsEditing(false)
  }

  const personFaces = faces.filter((f) => f.person_id === person.person_id)

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Prototype face — click to expand/collapse */}
        <div
          className={`${protoSizeMap[gridSize]} rounded-full overflow-hidden bg-secondary flex-shrink-0 cursor-pointer transition-all`}
          onClick={() => setExpanded(!expanded)}
        >
          {person.prototype_crop_url ? (
            <img
              src={signedSrc(person.prototype_crop_url)}
              alt={person.name ?? 'Unknown'}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <User size={gridSize === 'small' ? 20 : gridSize === 'large' ? 40 : 28} className="text-muted-foreground" />
            </div>
          )}
        </div>

        {/* Name */}
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="flex items-center gap-1.5">
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="flex-1 px-2 py-1 rounded bg-secondary border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-cyan-500"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName()
                  if (e.key === 'Escape') setIsEditing(false)
                }}
              />
              <button
                onClick={handleSaveName}
                className="text-green-500 hover:text-green-400"
              >
                <Check size={14} />
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium text-foreground truncate">
                {person.name || 'Unknown Person'}
              </span>
              <button
                onClick={() => {
                  setEditName(person.name ?? '')
                  setIsEditing(true)
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <Edit2 size={12} />
              </button>
            </div>
          )}
          <span className="text-xs text-muted-foreground">
            {person.face_count} face{person.face_count !== 1 ? 's' : ''} in{' '}
            {person.image_count} image{person.image_count !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => onFindImages(person.person_id)}
            className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground hover:text-cyan-400 transition-colors"
            title="Find images"
          >
            <Search size={14} />
          </button>
          <button
            onClick={() => onMerge(person.person_id)}
            className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
            title="Merge with another person"
          >
            <Merge size={14} />
          </button>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {/* Expanded face grid with hover preview */}
      {expanded && (
        <div className="px-4 pb-3 border-t border-border pt-3">
          <div className="flex flex-wrap gap-2">
            {personFaces.map((face) => (
              <div
                key={face.face_id}
                className={`${faceSizeMap[gridSize]} rounded-lg overflow-hidden bg-secondary cursor-pointer`}
                onMouseEnter={(e) => handleMouseEnter(face.face_id, signedSrc(face.crop_url), e.currentTarget)}
                onMouseLeave={handleMouseLeave}
                onClick={() => {
                  handlePreviewLeave()
                  onViewFace?.(face.face_id)
                }}
              >
                <img
                  src={signedSrc(face.crop_url)}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
          {personFaces.length === 0 && (
            <p className="text-xs text-muted-foreground">
              No faces loaded. Faces appear on the All tab.
            </p>
          )}
        </div>
      )}

      {/* Hover preview portal */}
      {hoverState && (
        <FaceHoverPreview
          cropUrl={hoverState.cropUrl}
          faceId={hoverState.faceId}
          triggerRect={hoverState.triggerRect}
          onMouseEnter={handlePreviewEnter}
          onMouseLeave={handlePreviewLeave}
          onRemove={onRemoveFace ? (fid) => {
            onRemoveFace(fid)
            handlePreviewLeave()
          } : undefined}
          onMakePrimary={onMakePrimary ? (fid) => {
            onMakePrimary(person.person_id, fid)
            handlePreviewLeave()
          } : undefined}
        />
      )}
    </div>
  )
}
